import { Link, useParams } from "react-router-dom";
import { useAgentContext } from "../../context/agent";
import { Alert, Button, Card, Form, Spinner, Row, Col, Toast } from "react-bootstrap";
import { FormEvent, useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { addWhatsAppCrendentials } from "../../api/agent";
import { FaWhatsapp, FaEdit, FaTimes, FaCopy, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';

function WhatsApp() {
    const [accessToken, setAccessToken] = useState("");
    const [phoneNumberId, setPhoneNumberId] = useState("");
    const [showToast, setShowToast] = useState(false);
    const [errorAlert, setErrorAlert] = useState("");
    const [disabled, setDisabled] = useState(true);
    const [isVerifying, setIsVerifying] = useState(false);

    const { agentId } = useParams();
    const { getAgentById } = useAgentContext();
    const agent = getAgentById(agentId || "");

    const apiURL = process.env.REACT_APP_API_URL;
    const webhookURL = `${apiURL}whatsapp/webhook/${agent?.accessKey}`;

    const queryClient = useQueryClient();
    const { mutateAsync: addWhatsAppAccount, isLoading } = useMutation(addWhatsAppCrendentials, {
        onSuccess: () => {
            queryClient.invalidateQueries(['Agents']);
        },
        onError: (error) => {
            setErrorAlert("Unexpected error. Please sign out and try again.");
        }
    })

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!agent || !agent.accessKey) {
            setErrorAlert("Unexpected error. Please sign out and try again.");
            return
        }
        setErrorAlert("");
        addWhatsAppAccount({
            apiKey: agent.accessKey,
            accessToken,
            phoneNumberId
        })
    }

    useEffect(() => {
        if (agent) {
            setAccessToken(agent.whatsAppAcessToken || "");
            setPhoneNumberId(agent.whatsAppPhoneId || "");
            setDisabled(agent.whatsAppAcessToken !== undefined && agent.whatsAppPhoneId !== undefined);
            if (agent.whatsAppWebhookVerified) setIsVerifying(false);
        }
    }, [agent]);

    useEffect(() => {
        if (!isVerifying) return;

        const timer = setTimeout(() => {
            queryClient.invalidateQueries(['Agents']);
            setIsVerifying(false);
        }, 5000);

        return () => clearTimeout(timer);
    }, [isVerifying]);

    return (
        <Row>
            <Col>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                    {/* Header */}
                    <div className="d-flex align-items-center mb-3">
                        <FaWhatsapp size={28} style={{ color: '#25D366', marginRight: '12px' }} />
                        <h4 style={{ color: '#fff', margin: 0 }}>WhatsApp Integration</h4>
                    </div>

                    {/* Info Alert */}
                    <Alert 
                        style={{ 
                            backgroundColor: '#1b2d1b', 
                            borderColor: '#25D366', 
                            color: '#b8daff',
                            marginBottom: '24px'
                        }}
                    >
                        For a step-by-step guide on how to integrate WhatsApp Business App with this application,
                        please visit our <Link 
                            to="/whatsapp" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            style={{ color: '#25D366', textDecoration: 'underline' }}
                        >
                            WhatsApp Integration Guide
                        </Link>.
                    </Alert>

                    {/* WhatsApp Credentials */}
                    <Card style={{ backgroundColor: '#2d2d2d', borderColor: '#444' }}>
                        <Card.Header style={{ backgroundColor: '#333', borderBottom: '1px solid #444', padding: '16px' }}>
                            <div className="d-flex align-items-center justify-content-between">
                                <h5 style={{ color: '#fff', margin: 0 }}>WhatsApp Credentials</h5>
                                <Button
                                    size="sm"
                                    onClick={() => setDisabled(!disabled)}
                                    style={{
                                        backgroundColor: disabled ? '#25D366' : '#6c757d',
                                        borderColor: disabled ? '#25D366' : '#6c757d',
                                        color: '#fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    {disabled ? <FaEdit size={14} /> : <FaTimes size={14} />}
                                    {disabled ? "Edit" : "Cancel"}
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body style={{ padding: '24px' }}>
                            <p style={{ color: '#ccc', marginBottom: '20px' }}>
                                Please copy the Access Token and Phone Number ID from your WhatsApp app settings and paste them into the form below. These credentials are necessary to configure the WhatsApp integration for your agent.
                            </p>
                            <Form onSubmit={handleSubmit}>
                                <Form.Group controlId="accessToken" className="mb-3">
                                    <Form.Label style={{ color: '#fff' }}>Access Token</Form.Label>
                                    <Form.Control
                                        type="text"
                                        disabled={disabled}
                                        value={accessToken}
                                        onChange={e => setAccessToken(e.target.value)}
                                        style={{
                                            backgroundColor: disabled ? '#1a1a1a' : '#2d2d2d',
                                            borderColor: '#555',
                                            color: '#fff'
                                        }}
                                        placeholder="Enter your WhatsApp access token"
                                    />
                                </Form.Group>
                                <Form.Group controlId="phoneNumberId" className="mb-3">
                                    <Form.Label style={{ color: '#fff' }}>Phone Number ID</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={phoneNumberId}
                                        disabled={disabled}
                                        onChange={e => setPhoneNumberId(e.target.value)}
                                        style={{
                                            backgroundColor: disabled ? '#1a1a1a' : '#2d2d2d',
                                            borderColor: '#555',
                                            color: '#fff'
                                        }}
                                        placeholder="Enter your phone number ID"
                                    />
                                </Form.Group>
                                <Button
                                    type="submit"
                                    disabled={disabled || isLoading}
                                    style={{
                                        backgroundColor: disabled ? '#555' : '#28a745',
                                        borderColor: disabled ? '#555' : '#28a745',
                                        color: '#fff'
                                    }}
                                >
                                    {isLoading && <Spinner animation="border" size="sm" style={{ marginRight: '8px' }} />}
                                    Save Credentials
                                </Button>
                            </Form>
                            {errorAlert && (
                                <Alert
                                    variant="danger"
                                    className="mt-3"
                                    style={{
                                        backgroundColor: '#2d1b1b',
                                        borderColor: '#dc3545',
                                        color: '#ff6b6b'
                                    }}
                                >
                                    {errorAlert}
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Webhook Configuration */}
                    {agent?.whatsAppVerifyToken && (
                        <>
                            <Card style={{ backgroundColor: '#2d2d2d', borderColor: '#444' }}>
                                <Card.Header style={{ backgroundColor: '#333', borderBottom: '1px solid #444', padding: '16px' }}>
                                    <h5 style={{ color: '#fff', margin: 0 }}>Webhook Configuration</h5>
                                </Card.Header>
                                <Card.Body style={{ padding: '24px' }}>
                                    <p style={{ color: '#ccc', marginBottom: '20px' }}>
                                        Please copy the Webhook URL and verify token below and paste it into your WhatsApp app settings.
                                        This URL and token are necessary to receive messages from WhatsApp.
                                    </p>

                                    <div style={{ marginBottom: '20px' }}>
                                        <h6 style={{ color: '#fff', marginBottom: '8px' }}>Callback URL</h6>
                                        <div style={{
                                            backgroundColor: '#1a1a1a',
                                            padding: '16px',
                                            borderRadius: '8px',
                                            border: '1px solid #555'
                                        }}>
                                            <code style={{
                                                color: '#fff',
                                                fontSize: '14px',
                                                wordBreak: 'break-all',
                                                display: 'block',
                                                marginBottom: '12px'
                                            }}>
                                                {webhookURL}
                                            </code>
                                            <Button
                                                size="sm"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(webhookURL);
                                                    setShowToast(true);
                                                }}
                                                style={{
                                                    backgroundColor: '#25D366',
                                                    borderColor: '#25D366',
                                                    color: '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <FaCopy size={12} />
                                                Copy URL
                                            </Button>
                                        </div>
                                    </div>

                                    <div>
                                        <h6 style={{ color: '#fff', marginBottom: '8px' }}>Verify Token</h6>
                                        <div style={{
                                            backgroundColor: '#1a1a1a',
                                            padding: '16px',
                                            borderRadius: '8px',
                                            border: '1px solid #555'
                                        }}>
                                            <code style={{
                                                color: '#fff',
                                                fontSize: '14px',
                                                wordBreak: 'break-all',
                                                display: 'block',
                                                marginBottom: '12px'
                                            }}>
                                                {agent.whatsAppVerifyToken}
                                            </code>
                                            <Button
                                                size="sm"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(agent.whatsAppVerifyToken || "");
                                                    setShowToast(true);
                                                }}
                                                style={{
                                                    backgroundColor: '#25D366',
                                                    borderColor: '#25D366',
                                                    color: '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <FaCopy size={12} />
                                                Copy Token
                                            </Button>
                                        </div>
                                    </div>
                                </Card.Body>
                            </Card>

                            {/* Verification Status */}
                            <Card style={{ backgroundColor: '#2d2d2d', borderColor: '#444' }}>
                                <Card.Header style={{ backgroundColor: '#333', borderBottom: '1px solid #444', padding: '16px' }}>
                                    <h5 style={{ color: '#fff', margin: 0 }}>Verification Status</h5>
                                </Card.Header>
                                <Card.Body style={{ padding: '24px' }}>
                                    {agent.whatsAppWebhookVerified ? (
                                        <Alert
                                            style={{
                                                backgroundColor: '#1b2d1b',
                                                borderColor: '#28a745',
                                                color: '#81c784',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}
                                        >
                                            <FaCheckCircle />
                                            Webhook Verified Successfully
                                        </Alert>
                                    ) : (
                                        <>
                                            <Alert
                                                style={{
                                                    backgroundColor: '#2d1b1b',
                                                    borderColor: '#dc3545',
                                                    color: '#ff6b6b',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '8px',
                                                    marginBottom: '16px'
                                                }}
                                            >
                                                <FaExclamationTriangle />
                                                Webhook Not Verified
                                            </Alert>
                                            <p style={{ color: '#ccc', marginBottom: '16px' }}>
                                                Please verify the webhook on your WhatsApp app on Meta before clicking the button below.
                                            </p>
                                            <Button
                                                onClick={() => setIsVerifying(true)}
                                                disabled={isVerifying}
                                                style={{
                                                    backgroundColor: '#007bff',
                                                    borderColor: '#007bff',
                                                    color: '#fff'
                                                }}
                                            >
                                                {isVerifying && <Spinner animation="border" size="sm" style={{ marginRight: '8px' }} />}
                                                {isVerifying ? "Checking..." : "Refresh Status"}
                                            </Button>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </>
                    )}

                    {/* Toast for copy confirmation */}
                    <Toast
                        onClose={() => setShowToast(false)}
                        show={showToast}
                        delay={2000}
                        autohide
                        style={{
                            position: 'fixed',
                            top: '20px',
                            right: '20px',
                            zIndex: 1000,
                            backgroundColor: '#2d2d2d',
                            borderColor: '#28a745'
                        }}
                    >
                        <Toast.Body style={{ color: '#28a745' }}>
                            Copied to clipboard!
                        </Toast.Body>
                    </Toast>
                </div>
            </Col>
        </Row>
    )
}

export default WhatsApp